import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Trans } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Button,
  ContentLoader,
  currency,
  CustomChangeEvent,
  FieldChangeEvent,
  normalizeChangeHandler,
  RadioCardField,
  Text,
  useFormField,
} from '@frontend/design-system';
import { useCareCreditTranslations } from '../../../hooks/use-care-credit-translations';
import { CollectBaseStep } from '../atoms/step/collect-base-step';
import { CollectStepPerformanceWrapper } from '../atoms/step/collect-step-performance-wrapper';
import { CareCreditCollectMultiSteps } from '../collect-multi-step.types';
import { useCareCreditCollectContext } from '../context/care-credit-collect.context';
import { FinanceOptionDetails } from './step3-select-finance-option/finance-option-details';

const CURRENT_STEP: CareCreditCollectMultiSteps = 'select-financing-option';
const PREV_STEP: CareCreditCollectMultiSteps = 'payment-method-form';
const NEXT_STEP: CareCreditCollectMultiSteps = 'receipt-options';

const styles = {
  stepOverrides: css`
    .step__header {
      padding-bottom: ${theme.spacing(1)};
    }
  `,
  detailsStepOverrides: css`
    .step__footer button[type='button'] {
      text-transform: none;
    }
  `,
  root: css`
    position: relative;
  `,
  items: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${theme.spacing(2)};
  `,
  radioCardField: css`
    & > div {
      flex-direction: column;
    }
  `,
  optionWrapper: css`
    position: relative;
  `,
  option: css`
    width: 100%;
  `,
  optionDescription: css`
    width: calc(100% - 100px);
  `,
  optionDescriptionText: css`
    color: ${theme.colors.black};
    font-size: 10pt;
  `,
  optionAction: css`
    position: absolute;
    right: ${theme.spacing(2)};
    top: 50%;
    transform: translateY(-50%);
  `,
  grayBanner: css`
    padding: ${theme.spacing(2)};
    border-radius: ${theme.borderRadius.medium};
    display: flex;
    gap: ${theme.spacing(1)};
    align-items: center;
    background: ${theme.colors.neutral5};
    border: solid 1px ${theme.colors.neutral20};
    color: ${theme.colors.black};
  `,
};

export const Step3SelectFinanceOption = () => {
  return (
    <CollectStepPerformanceWrapper step={CURRENT_STEP}>
      <Step3SelectFinanceOptionBody />
    </CollectStepPerformanceWrapper>
  );
};

const Step3SelectFinanceOptionBody = () => {
  const {
    multiStep: { goPrevStep, goNextStep },
    isLoadingFinanceOptions,
    selectedFinanceOption,
    financeOptions,
    collectForm,
    invoice,
    setSelectedFinanceOption,
  } = useCareCreditCollectContext();

  const [optionDetailsId, setOptionDetailsId] = useState<string | undefined>(undefined);

  const { onChange: onChangeFieldProp, ...fieldProps } = useFormField({
    type: 'radio',
  });

  const deferredInterestOptions = useMemo(() => {
    return financeOptions.filter((fo) => fo.type === 'deferred_interest');
  }, [financeOptions]);

  const reducedAprOptions = useMemo(() => {
    return financeOptions.filter((fo) => fo.type === 'reduced_apr');
  }, [financeOptions]);

  const { t } = useCareCreditTranslations();

  const onClickNext = useCallback(() => {
    goNextStep({ orStep: NEXT_STEP });
  }, [goNextStep]);

  const onSelectFinanceOption = useCallback(
    (e?: FieldChangeEvent) => {
      normalizeChangeHandler(({ value }) => {
        onChangeFieldProp(e);
        setSelectedFinanceOption(financeOptions.find((option) => option.id === value));
      })(e as CustomChangeEvent);
    },
    [financeOptions, setSelectedFinanceOption, onChangeFieldProp]
  );

  const onGoBack = useCallback(() => {
    goPrevStep({ orStep: PREV_STEP });
  }, [goPrevStep]);

  const onClickCloseDetails = () => {
    setOptionDetailsId(undefined);
  };

  const onClickSeeDetails = (optionId: string) => {
    setOptionDetailsId(optionId);
  };

  const optionDetails = financeOptions.find((option) => option.id === optionDetailsId);
  if (optionDetailsId && optionDetails) {
    /**
     * Return details view
     */

    return (
      <CollectBaseStep
        id={CURRENT_STEP}
        label={optionDetails.title}
        css={styles.detailsStepOverrides}
        footer={{
          primary: null,
          back: {
            children: t('Back to Promotions'),
            iconName: 'back',
          },
          showBackButton: true,
          onClickBack: onClickCloseDetails,
        }}
      >
        <FinanceOptionDetails financeOption={optionDetails} />
      </CollectBaseStep>
    );
  }

  const amountInDollars = Number(collectForm.fields.purchaseAmount) || (invoice?.billedAmount || 0) / 100;

  return (
    <CollectBaseStep
      id={CURRENT_STEP}
      label={t('Select Promotion')}
      css={styles.stepOverrides}
      footer={{
        primary: {
          children: t('Next'),
          disabled: !selectedFinanceOption || isLoadingFinanceOptions,
        },
        onClickPrimary: onClickNext,
        back: {
          children: t('Back'),
          iconName: 'back',
        },
        showBackButton: true,
        onClickBack: onGoBack,
      }}
    >
      <div css={styles.root}>
        {isLoadingFinanceOptions && <ContentLoader />}
        <div className='flex-items' css={styles.items}>
          <div>
            <Text>
              {t('Please share Promotional Financing Disclosures for Consumers (online or in-office brochure).')}
            </Text>
          </div>

          <div>
            <div className='gray-banner' css={styles.grayBanner}>
              <div>
                <Icon name='pay' height={16} />
              </div>
              <div>
                <Text size='medium' style={{ lineHeight: 1 }}>
                  <Trans t={t}>
                    Amount Financed: <b>{currency(amountInDollars, { decimals: 2 })}</b>
                  </Trans>
                </Text>
              </div>
            </div>
          </div>
          {deferredInterestOptions.length > 0 && (
            <>
              <div>
                <Text weight='bold'>{t('Deferred Interest')}</Text>
              </div>
              <div>
                <RadioCardField
                  {...fieldProps}
                  onChange={onSelectFinanceOption}
                  name='financeOption'
                  className='select-finance-option--radio-card-field'
                  css={styles.radioCardField}
                >
                  {deferredInterestOptions.map((option) => {
                    return (
                      <div css={styles.optionWrapper} key={option.id}>
                        <RadioCardField.Option
                          value={option.id}
                          title={option.title}
                          css={styles.option}
                          description={
                            <div key={`${option.id}-description`} css={styles.optionDescription}>
                              {option.type === 'deferred_interest' && (
                                <div css={styles.optionDescriptionText}>
                                  <div>
                                    {t('{{months}} Months', {
                                      months: option.period,
                                    })}
                                  </div>
                                  <div>
                                    {t('{{amount}}/mo suggested payments', {
                                      amount: currency(option.requiredPayment, { decimals: 0 }),
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          }
                        />

                        <div css={styles.optionAction}>
                          <Button variant='tertiary' onClick={() => onClickSeeDetails(option.id)}>
                            <span>{t('See Details')}</span>
                            <Icon name='caret-right' height={16} width={16} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </RadioCardField>
              </div>
            </>
          )}

          {reducedAprOptions.length > 0 && (
            <>
              <div>
                <Text weight='bold'>{t('Fixed Pay Reduced APR')}</Text>
              </div>
              <div>
                <RadioCardField
                  {...fieldProps}
                  onChange={onSelectFinanceOption}
                  name='financeOption'
                  className='select-finance-option--radio-card-field'
                  css={styles.radioCardField}
                >
                  {reducedAprOptions.map((option) => {
                    return (
                      <div css={styles.optionWrapper} key={option.id}>
                        <RadioCardField.Option
                          value={option.id}
                          title={option.title}
                          css={styles.option}
                          description={
                            <div key={`${option.id}-description`} css={styles.optionDescription}>
                              <div>
                                {t('{{months}} Months @ {{interestRate}}%', {
                                  months: option.period,
                                  interestRate: option.interestRate,
                                })}
                              </div>
                              <div>
                                {t('{{amount}}/mo suggested payments', {
                                  amount: currency(option.requiredPayment, { decimals: 0 }),
                                })}
                              </div>
                            </div>
                          }
                        />

                        <div css={styles.optionAction}>
                          <Button variant='tertiary' onClick={() => onClickSeeDetails(option.id)}>
                            <span>{t('See Details')}</span>
                            <Icon name='caret-right' />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </RadioCardField>
              </div>
            </>
          )}
        </div>
      </div>
    </CollectBaseStep>
  );
};
